import React, { ReactNode, useMemo } from 'react';

import { UiComponent } from 'components';
import { tableWrap } from 'styles/table';
import { IStickerListItem } from 'types';
import useModal from 'hooks/useModal';
import Form from '../Form';
import Grant from '../Grant';
import Holders from '../Holders';
import * as ListStyle from './style';

const COLS: string[] = [
  '번호',
  '이미지',
  '잠금 이미지',
  '이름',
  '획득 방법',
  '보유자 수',
  '관리',
];

interface Props {
  data: IStickerListItem[];
}

function Thumbnail({ url, alt }: { url: string | null; alt: string }) {
  if (!url) return <div css={ListStyle.emptyThumbnail}>없음</div>;

  return (
    <div css={ListStyle.thumbnail}>
      <img src={url} alt={alt} />
    </div>
  );
}

function List({ data }: Props) {
  const { handleModal } = useModal();

  const openEdit = (sticker: IStickerListItem) =>
    handleModal('스티커 수정', <Form sticker={sticker} />, <></>);

  const openGrant = (sticker: IStickerListItem) =>
    handleModal('스티커 지급 / 회수', <Grant sticker={sticker} />, <></>);

  const openHolders = (sticker: IStickerListItem) =>
    handleModal('보유자 목록', <Holders sticker={sticker} />, <></>);

  const renderer: Record<
    (typeof COLS)[number],
    (data: IStickerListItem & { row: number }) => ReactNode
  > = useMemo(
    () => ({
      번호: ({ id }: IStickerListItem) => (
        <p css={ListStyle.itemNo}>{`ITEM NO.${String(id).padStart(3, '0')}`}</p>
      ),
      이미지: ({ imageUrl, name }: IStickerListItem) => (
        <Thumbnail url={imageUrl} alt={name} />
      ),
      '잠금 이미지': ({ lockedImageUrl, name }: IStickerListItem) => (
        <Thumbnail url={lockedImageUrl} alt={`${name} 잠금`} />
      ),
      이름: ({ name, code }: IStickerListItem) => (
        <div css={{ textAlign: 'left' }}>
          <p>{name}</p>
          <p css={(theme) => ({ fontSize: 12, color: theme.color.grey })}>
            {code}
          </p>
        </div>
      ),
      '획득 방법': ({ acquisitionGuide }: IStickerListItem) => (
        <p css={ListStyle.guide}>{acquisitionGuide || '-'}</p>
      ),
      '보유자 수': (sticker: IStickerListItem) => (
        <p
          css={ListStyle.holderCount}
          title="보유자 목록 보기"
          onClick={() => openHolders(sticker)}
        >
          {sticker.holderCount.toLocaleString('ko')}명
        </p>
      ),
      관리: (sticker: IStickerListItem) => (
        <div css={ListStyle.actions}>
          <UiComponent.Button
            variant="border"
            color="primary"
            onClick={() => openEdit(sticker)}
          >
            수정
          </UiComponent.Button>
          <UiComponent.Button
            variant="contained"
            color="primary"
            onClick={() => openGrant(sticker)}
          >
            지급
          </UiComponent.Button>
        </div>
      ),
    }),
    [],
  );

  const rows = data.map((s) => ({ ...s, rowKey: s.id }));

  return (
    <UiComponent.Box>
      <div css={tableWrap}>
        <UiComponent.Table
          cols={COLS}
          renderer={renderer}
          data={rows}
          sticky
          empty="등록된 스티커가 없습니다"
        />
      </div>
    </UiComponent.Box>
  );
}

export default List;
